// What a trap is doing, as the status tag shows it.
//
// The device shadow's `state_machine` says more than the online/offline status
// the backend derives from it: a trap that is downloading firmware is busy, not
// broken, and an operator standing next to it needs to tell the two apart. The
// derived status stays authoritative for alerts and for anything counting
// online traps; this is only how a trap is labelled.

export type DeviceStatusTag =
  "online" | "capturing" | "updating" | "booting" | "offline";

export const DEVICE_STATUS_LABELS: Record<DeviceStatusTag, string> = {
  online: "Online",
  capturing: "Capturing",
  updating: "Updating",
  booting: "Booting",
  offline: "Offline",
};

/** The shadow state the backend writes when a device's MQTT last will fires. */
export const OFF_STATE = "OFF";

const TAG_BY_STATE: Record<string, DeviceStatusTag> = {
  IDLE: "online",
  CAPTURING: "capturing",
  UPDATING: "updating",
  ESP_UPDATING: "updating",
  BOOTING: "booting",
  [OFF_STATE]: "offline",
};

/**
 * The tag for a device.
 *
 * `state` decides when the backend sends one; a state this build does not know
 * reads as Offline, the same answer the server's derived status gives it. A
 * device with no `state` - a backend that predates the field - falls back to the
 * online/offline `status` it has always had.
 */
export const deviceStatusTag = (device: {
  state?: string | null;
  status?: string | null;
}): DeviceStatusTag => {
  const state = device.state?.trim().toUpperCase();
  if (state) return TAG_BY_STATE[state] ?? "offline";
  return (device.status || "").toLowerCase() === "online"
    ? "online"
    : "offline";
};

interface StatefulDevice {
  uuid?: string;
  status?: unknown;
  status_updated_at?: unknown;
  state?: string | null;
  state_updated_at?: string | null;
  state_version?: number;
}

const stateTime = (device: StatefulDevice): number =>
  device.state_updated_at ? Date.parse(device.state_updated_at) : NaN;

const carryOver = <T extends StatefulDevice>(
  cached: StatefulDevice,
  fetched: T,
): T =>
  Object.assign(fetched, {
    status: cached.status,
    status_updated_at: cached.status_updated_at,
    state: cached.state,
    state_updated_at: cached.state_updated_at,
    state_version: cached.state_version,
  });

/**
 * Carries a cached state over a fetched one that is older than it.
 *
 * Every (re)connect of the realtime socket refetches the devices, and a live
 * event can land while that request is in flight. The response was read before
 * the event happened, so letting it replace the cache would put the old state
 * back - and with the socket up nothing polls, so it would stay until the next
 * event. When both sides carry the shadow `state_version`, it orders them
 * exactly. Otherwise both carry the shadow timestamp of the event that set their
 * `state` as `state_updated_at`, which orders them; a fetched copy without one
 * comes from a backend that predates state and is taken as it is. The timestamp
 * only counts whole seconds, so a tie does not order them: a cached copy an
 * event was patched into (it carries `state_version`) is kept over a fetch from
 * the same second, which may have been read before the event.
 *
 * Mutates and returns `fetched`, for an RTK Query `merge`.
 */
export const keepNewerState = <T extends StatefulDevice>(
  cached: StatefulDevice | undefined,
  fetched: T,
): T => {
  if (!cached) return fetched;
  const cachedVersion = cached.state_version;
  const fetchedVersion = fetched.state_version;
  if (typeof cachedVersion === "number" && typeof fetchedVersion === "number") {
    return cachedVersion > fetchedVersion ? carryOver(cached, fetched) : fetched;
  }
  const cachedAt = stateTime(cached);
  const fetchedAt = stateTime(fetched);
  if (Number.isNaN(cachedAt) || Number.isNaN(fetchedAt)) return fetched;
  const patched = typeof cachedVersion === "number";
  if (cachedAt < fetchedAt || (cachedAt === fetchedAt && !patched)) {
    return fetched;
  }
  return carryOver(cached, fetched);
};

/** keepNewerState for every device in a fetched list, matched by uuid. */
export const keepNewerStates = <T extends StatefulDevice>(
  cached: StatefulDevice[] | undefined,
  fetched: T[],
): T[] => {
  const byUuid = new Map((cached ?? []).map((d) => [d.uuid, d]));
  fetched.forEach((device) => keepNewerState(byUuid.get(device.uuid), device));
  return fetched;
};
